// src/ine/pages/INECountriesPage.jsx
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import AutoDismissAlert from "../../../components/autoDismiss";
import { API_URLS } from "../../../api/apiConfig";
import { useINE } from "../adminINECountriesContext";
import { getWithAuth, postWithAuth } from "../../../api/api";
import ModalINEUpdate from "../components/modalINEUpdate";

const INECountriesPage = () => {
  const INEContext = useINE();

  // ------- Opciones de canal (select global) -------
  const [channelOptions, setChannelOptions] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  // ------- Países -------
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesSearch, setCountriesSearch] = useState("");
  const [countriesChannel, setCountriesChannel] = useState("");
  const [countriesAssigned, setCountriesAssigned] = useState(""); // "", "true", "false"
  const [countriesPage, setCountriesPage] = useState(1);
  const [countriesLimit, setCountriesLimit] = useState(10);
  const [countriesTotal, setCountriesTotal] = useState(0);

  // ------- Provincias / Islas (NUTS III) -------
  const [provinces, setProvinces] = useState([]);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [provincesSearch, setProvincesSearch] = useState("");
  const [provincesChannel, setProvincesChannel] = useState("");
  const [provincesAssigned, setProvincesAssigned] = useState(""); // "", "true", "false"
  const [provincesPage, setProvincesPage] = useState(1);
  const [provincesLimit, setProvincesLimit] = useState(10);
  const [provincesTotal, setProvincesTotal] = useState(0);

  // Helpers
  const handleApiError = (err) => {
    console.error(err);
    INEContext.setMessage("Ha ocurrido un error conectando con el servidor");
    INEContext.setAlertType("danger");
    INEContext.openAlertHeader();
  };

  const fetchChannelOptions = async () => {
    setLoadingChannels(true);
    try {
      const res = await getWithAuth(API_URLS.GET_INE_CHANNEL_POSSIBLE_VALUES);
      const arr =
        res?.data?.channel_values || res?.data?.values || res?.data || [];
      setChannelOptions(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.warn("No se pudieron cargar los canales:", e);
    } finally {
      setLoadingChannels(false);
    }
  };

  const normalizeAssigned = (value) =>
    value === "" ? undefined : value === "true";

  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const filters = {
        page: countriesPage,
        limit: countriesLimit,
        search_text: countriesSearch || undefined,
        is_country: true,
        channel: countriesChannel || undefined,
        assigned: normalizeAssigned(countriesAssigned),
      };
      const res = await postWithAuth(API_URLS.LIST_INE_BY_FILTERS, filters);
      setCountries(res.data.ine_countries_provinces || []);
      setCountriesTotal(res.data.total ?? 0);
    } catch (e) {
      handleApiError(e);
    } finally {
      setCountriesLoading(false);
    }
  };

  const fetchProvinces = async () => {
    setProvincesLoading(true);
    try {
      const filters = {
        page: provincesPage,
        limit: provincesLimit,
        search_text: provincesSearch || undefined,
        is_country: false,
        channel: provincesChannel || undefined,
        assigned: normalizeAssigned(provincesAssigned),
      };
      const res = await postWithAuth(API_URLS.LIST_INE_BY_FILTERS, filters);
      setProvinces(res.data.ine_countries_provinces || []);
      setProvincesTotal(res.data.total ?? 0);
    } catch (e) {
      handleApiError(e);
    } finally {
      setProvincesLoading(false);
    }
  };

  // Cargar canales al montar
  useEffect(() => {
    fetchChannelOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar al cambiar paginación/limit
  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesPage, countriesLimit]);

  useEffect(() => {
    fetchProvinces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provincesPage, provincesLimit]);

  // Refrescar por señal global del contexto
  useEffect(() => {
    fetchCountries();
    fetchProvinces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [INEContext.refreshKey]);

  const handleUpdateClick = (row) => {
    INEContext.openUpdateModal?.(row.id, row);
  };

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar">
          <div
            id="kt_app_toolbar_container"
            className="app-container container-fluid d-flex flex-stack"
          >
            <div className="page-title d-flex flex-column justify-content-center me-3 mb-6 mb-lg-0">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center me-3 mb-3 my-0">
                Catálogo INE (Países y Provincias/Islas)
              </h1>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div
            id="kt_app_content_container"
            className="app-container container-fluid"
          >
            {INEContext.showAlertHeader && (
              <AutoDismissAlert
                message={INEContext.message}
                alertType={INEContext.alertType}
                onClose={INEContext.closeAlertHeader}
              />
            )}

            <div className="row">
              {/* -------- Card Países (full width) -------- */}
              <div className="col-12 mb-10">
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title">
                      <span className="card-label fw-bold text-gray-900">
                        Países
                      </span>
                    </h3>
                    <div className="card-toolbar" />
                  </div>

                  <div className="card-body py-4">
                    <Form
                      className="d-flex align-items-center mb-4 gap-3 flex-wrap"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setCountriesPage(1);
                        fetchCountries();
                      }}
                    >
                      <Form.Control
                        style={{ maxWidth: 320 }}
                        placeholder="Buscar país (source/channel/INE)"
                        type="text"
                        value={countriesSearch}
                        onChange={(e) => setCountriesSearch(e.target.value)}
                      />

                      <Form.Select
                        style={{ maxWidth: 240 }}
                        value={countriesChannel}
                        onChange={(e) => setCountriesChannel(e.target.value)}
                        disabled={loadingChannels}
                      >
                        <option value="">Todos los canales</option>
                        {channelOptions.map((ch) => (
                          <option key={ch} value={ch}>
                            {ch}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Select
                        style={{ maxWidth: 200 }}
                        value={countriesAssigned}
                        onChange={(e) => setCountriesAssigned(e.target.value)}
                      >
                        <option value="">Todos</option>
                        <option value="true">Asignados</option>
                        <option value="false">No asignados</option>
                      </Form.Select>

                      <a
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setCountriesPage(1);
                          fetchCountries();
                        }}
                      >
                        Aplicar filtros
                      </a>
                    </Form>

                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                            <th>Source</th>
                            <th>Channel</th>
                            <th>INE name</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {countriesLoading ? (
                            <tr>
                              <td colSpan={4} className="text-center py-5">
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">
                                    Cargando…
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ) : countries.length > 0 ? (
                            countries.map((c, idx) => (
                              <tr key={idx}>
                                <td>{c.source ?? "-"}</td>
                                <td>{c.channel ?? "-"}</td>
                                <td>{c.ine_name ?? "-"}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => handleUpdateClick(c)}
                                  >
                                    Actualizar
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-5">
                                No se encontraron países
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex align-items-center justify-content-between">
                      <Form.Select
                        style={{ width: 150 }}
                        value={countriesLimit}
                        onChange={(e) => {
                          setCountriesPage(1);
                          setCountriesLimit(Number(e.target.value));
                        }}
                      >
                        <option value="5">5 por página</option>
                        <option value="10">10 por página</option>
                        <option value="20">20 por página</option>
                      </Form.Select>

                      <nav>
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${
                              countriesPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setCountriesPage(countriesPage - 1)
                              }
                            >
                              Anterior
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link">
                              Página {countriesPage} de{" "}
                              {Math.max(
                                1,
                                Math.ceil(countriesTotal / countriesLimit)
                              )}
                            </span>
                          </li>
                          <li
                            className={`page-item ${
                              countriesPage * countriesLimit >= countriesTotal
                                ? "disabled"
                                : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setCountriesPage(countriesPage + 1)
                              }
                            >
                              Siguiente
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>

              {/* -------- Card Provincias / Islas (full width) -------- */}
              <div className="col-12 mb-10">
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title">
                      <span className="card-label fw-bold text-gray-900">
                        Provincias / Islas (NUTS III)
                      </span>
                    </h3>
                    <div className="card-toolbar" />
                  </div>

                  <div className="card-body py-4">
                    <Form
                      className="d-flex align-items-center mb-4 gap-3 flex-wrap"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setProvincesPage(1);
                        fetchProvinces();
                      }}
                    >
                      <Form.Control
                        style={{ maxWidth: 320 }}
                        placeholder="Buscar provincia/isla (source/channel/INE)"
                        type="text"
                        value={provincesSearch}
                        onChange={(e) => setProvincesSearch(e.target.value)}
                      />

                      <Form.Select
                        style={{ maxWidth: 240 }}
                        value={provincesChannel}
                        onChange={(e) => setProvincesChannel(e.target.value)}
                        disabled={loadingChannels}
                      >
                        <option value="">Todos los canales</option>
                        {channelOptions.map((ch) => (
                          <option key={ch} value={ch}>
                            {ch}
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Select
                        style={{ maxWidth: 200 }}
                        value={provincesAssigned}
                        onChange={(e) => setProvincesAssigned(e.target.value)}
                      >
                        <option value="">Todos</option>
                        <option value="true">Asignados</option>
                        <option value="false">No asignados</option>
                      </Form.Select>

                      <a
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setProvincesPage(1);
                          fetchProvinces();
                        }}
                      >
                        Aplicar filtros
                      </a>
                    </Form>

                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                            <th>Source</th>
                            <th>Channel</th>
                            <th>INE name</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {provincesLoading ? (
                            <tr>
                              <td colSpan={4} className="text-center py-5">
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">
                                    Cargando…
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ) : provinces.length > 0 ? (
                            provinces.map((p, idx) => (
                              <tr key={idx}>
                                <td>{p.source ?? "-"}</td>
                                <td>{p.channel ?? "-"}</td>
                                <td>{p.ine_name ?? "-"}</td>

                                <td>
                                  <button
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => handleUpdateClick(p)}
                                  >
                                    Actualizar
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-5">
                                No se encontraron provincias/islas
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex align-items-center justify-content-between">
                      <Form.Select
                        style={{ width: 150 }}
                        value={provincesLimit}
                        onChange={(e) => {
                          setProvincesPage(1);
                          setProvincesLimit(Number(e.target.value));
                        }}
                      >
                        <option value="5">5 por página</option>
                        <option value="10">10 por página</option>
                        <option value="20">20 por página</option>
                      </Form.Select>

                      <nav>
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${
                              provincesPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setProvincesPage(provincesPage - 1)
                              }
                            >
                              Anterior
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link">
                              Página {provincesPage} de{" "}
                              {Math.max(
                                1,
                                Math.ceil(provincesTotal / provincesLimit)
                              )}
                            </span>
                          </li>
                          <li
                            className={`page-item ${
                              provincesPage * provincesLimit >= provincesTotal
                                ? "disabled"
                                : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setProvincesPage(provincesPage + 1)
                              }
                            >
                              Siguiente
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* row */}
          </div>
        </div>
      </div>

      {INEContext.updateModalOpen && (
        <ModalINEUpdate
          ine_id={INEContext.selectedId}
          onClose={() => {
            INEContext.closeUpdateModal();
            fetchCountries();
            fetchProvinces();
          }}
        />
      )}
    </>
  );
};

export default INECountriesPage;
